// Basic
// React and Router
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
// Store & Actions
import actionsSubscriptions from '@/app/modules/subscriptions/api/actionsSubscriptions';
// Components
import Page from '@/common-utils/components/structure/page/Page';
import Spinner from '@/common-utils/components/renders/spinner/Spinner';
import PosTerminalDetailsTab from '@/app/modules/terminals/components/tabs/PosTerminalDetailsTab';
import Entity from '@/common-utils/components/renders/entity/Entity';
import EventLogsTable from '@/app/modules/terminals/components/tables/EventLogsTable';
import TerminalContractsTable from '@/app/modules/contracts/components/tables/TerminalContractsTable';
import TerminalRemoteCollectionsTable from '@/app/modules/remoteCollections/components/tables/TerminalRemoteCollectionsTable';
import TransactionsTable from '@/app/modules/remoteCollections/components/tables/TransactionsTable';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/common-utils/components/ui/tabs';
// Common Utilities & Enums
import RouteUrls from '../../enums/RouteUrls';
import SUBSCRIPTION_AND_TERMINAL_DETAILS_TABS from '../../enums/tabs/SubscriptionAndTerminalDetailsTabs';
import POS_TERMINALS_TABS from '../../enums/tabs/PosTerminalsTabs';
import { extractQueriesFromUrl } from '@/common-utils/functions/restUtils';
import useTabManager from '../../hooks/useTabManager';
import generateNestedFieldAccessor from '@/common-utils/functions/generateNestedFieldAccessor';
import eventLogFields from '@/app/modules/terminals/fields/eventLogFields';
import useSessionStore from '@/app/store/session';
import setFiltersEventName from '@/common-utils/components/renders/dataTable/utils/setFiltersEventName';
import { POS_TERMINALS } from '@/app/modules/terminals/api/apiUrls';
import Permissions from '@/app/permissions/Permissions';

/**
 * @returns {JSX} SubscriptionAndTerminalDetails
 */
function SubscriptionAndTerminalDetails() {

    // URL Params
    const navigate = useNavigate();
    const location = useLocation();
    const { view, viewDetails } = extractQueriesFromUrl(location.search);
    const urlParams = extractQueriesFromUrl(location.search);
    const acceptanceAgreementNumber = urlParams[eventLogFields.FINANCIAL_INFORMATION.ACCEPTANCE_SYSTEM_INFORMATION.ACCEPTANCE_AGREEMENT_NUMBER.key];
    const applicationId = urlParams[eventLogFields.FINANCIAL_INFORMATION.ACCEPTANCE_SYSTEM_INFORMATION.APPLICATION_ID.key];

    // State
    const [activeTab, setActiveTab] = useState(
        Object.values(SUBSCRIPTION_AND_TERMINAL_DETAILS_TABS)
            .find(tab => tab.name === viewDetails)?.name ?? SUBSCRIPTION_AND_TERMINAL_DETAILS_TABS.DEFAULT.name
    );
    const [showTableConnections, setShowTableConnections] = useState(false);
    const [customQuery, setCustomQuery] = useState({});

    // Store
    const { permissions } = useSessionStore(s => s.session);

    // URL Params
    const { subscriptionId } = useParams();

    // API Calls
    const { data: dataSubscription,
        isLoading: isLoadingSubscription,
        refetch } = actionsSubscriptions.getSubscription(subscriptionId);

    // Hooks
    useEffect(() => {
        if (!acceptanceAgreementNumber && !applicationId) {
            setShowTableConnections(true);
        } else {
            setCustomQuery({
                filters: [
                    {
                        key: generateNestedFieldAccessor(
                            eventLogFields.FINANCIAL_INFORMATION.ACCEPTANCE_SYSTEM_INFORMATION.ACCEPTANCE_AGREEMENT_NUMBER),
                        value: acceptanceAgreementNumber
                    },
                    {
                        key: generateNestedFieldAccessor(
                            eventLogFields.FINANCIAL_INFORMATION.ACCEPTANCE_SYSTEM_INFORMATION.APPLICATION_ID),
                        value: applicationId
                    }
                ]
            });
        }
    }, [acceptanceAgreementNumber, applicationId]);

    // Handles tab selection
    useTabManager({
        pathname: location.pathname,
        view: view,
        viewDetails: viewDetails,
        activeTab: activeTab,
        setActiveTab: setActiveTab,
        tabs: SUBSCRIPTION_AND_TERMINAL_DETAILS_TABS,
        navigate: navigate
    });

    // Transactions export props
    const transactionsExportProps = {
        permissions: [
            Permissions.EXPORT_POS_TERMINAL_TRANSACTIONS.name
        ],
        api: POS_TERMINALS.EXPORT_TRANSACTIONS,
        uuid: dataSubscription?.posTerminal?.uuid
    };

    return (
        <Page>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <Page.Header
                    route={RouteUrls.SUBSCRIPTION_DETAILS}
                    prevView={view || POS_TERMINALS_TABS.SUBSCRIPTIONS.name}
                    i18nData={{ serialNumber: dataSubscription?.posTerminal?.serialNumber }}
                    isLoading={isLoadingSubscription}
                    routeBase={RouteUrls.SUBSCRIPTIONS}
                >
                    <TabsList>
                        <TabsTrigger tab={SUBSCRIPTION_AND_TERMINAL_DETAILS_TABS.DETAILS} activeTab={activeTab} permissions={permissions} >
                            <Entity entity='page.subscription.tabs.details' />
                        </TabsTrigger>
                        <TabsTrigger tab={SUBSCRIPTION_AND_TERMINAL_DETAILS_TABS.CONTRACTS} activeTab={activeTab} permissions={permissions} >
                            <Entity entity='page.subscription.tabs.contracts' />
                        </TabsTrigger>
                        <TabsTrigger tab={SUBSCRIPTION_AND_TERMINAL_DETAILS_TABS.CONNECTIONS} activeTab={activeTab} permissions={permissions} >
                            <Entity entity='page.subscription.tabs.connections' />
                        </TabsTrigger>
                        <TabsTrigger tab={SUBSCRIPTION_AND_TERMINAL_DETAILS_TABS.TRANSACTIONS} activeTab={activeTab} permissions={permissions} >
                            <Entity entity='page.subscription.tabs.transactions' />
                        </TabsTrigger>
                        <TabsTrigger tab={SUBSCRIPTION_AND_TERMINAL_DETAILS_TABS.REMOTE_COLLECTIONS} activeTab={activeTab} permissions={permissions} >
                            <Entity entity='page.subscription.tabs.remote_collections' />
                        </TabsTrigger>
                    </TabsList>
                </Page.Header>
                {
                    isLoadingSubscription ?
                        <Spinner className={'full-screen-loading'} />
                        :
                        <Page.Body dataCy='page-subscription-details'>
                            <TabsContent tab={SUBSCRIPTION_AND_TERMINAL_DETAILS_TABS.DETAILS} permissions={permissions}>
                                {
                                    dataSubscription &&
                                    <PosTerminalDetailsTab subscriptionAndTerminal={dataSubscription} refetch={refetch} />
                                }
                            </TabsContent>
                            <TabsContent tab={SUBSCRIPTION_AND_TERMINAL_DETAILS_TABS.CONTRACTS} permissions={permissions}>
                                <TerminalContractsTable
                                    filtersEventName={setFiltersEventName('terminal_contracts')}
                                    posTerminalId={dataSubscription.posTerminal.uuid}
                                />
                            </TabsContent>
                            <TabsContent tab={SUBSCRIPTION_AND_TERMINAL_DETAILS_TABS.CONNECTIONS} permissions={permissions}>
                                {showTableConnections && (
                                    <EventLogsTable
                                        filtersEventName={setFiltersEventName('event_logs')}
                                        posTerminalId={dataSubscription.posTerminal.uuid}
                                        customQuery={customQuery}
                                    />
                                )}
                            </TabsContent>
                            <TabsContent tab={SUBSCRIPTION_AND_TERMINAL_DETAILS_TABS.TRANSACTIONS} permissions={permissions}>
                                <TransactionsTable
                                    filtersEventName={setFiltersEventName('transactions')}
                                    posTerminalId={dataSubscription.posTerminal.uuid}
                                    exportProps={transactionsExportProps}
                                />
                            </TabsContent>
                            <TabsContent tab={SUBSCRIPTION_AND_TERMINAL_DETAILS_TABS.REMOTE_COLLECTIONS} permissions={permissions}>
                                <TerminalRemoteCollectionsTable
                                    filtersEventName={setFiltersEventName('remote_collections')}
                                    posTerminalId={dataSubscription.posTerminal.uuid}
                                />
                            </TabsContent>
                        </Page.Body>
                }
            </Tabs>
        </Page>
    );
}

export default SubscriptionAndTerminalDetails;